import React, { useState, useEffect } from 'react';
import {
  Typography, Button, Box, TextField, Alert, CircularProgress,
  FormControlLabel, Checkbox, Tabs, Tab, useTheme, useMediaQuery,
} from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import EditIcon from '@mui/icons-material/Edit';
import ModalWrapper from '../components/ModalWrapper';
import PriceScanner from '../components/PriceScanner';
import { apiFetch } from '../apiFetch';

interface EnterPriceModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (price: any) => void;
  groceryId: number;
  groceryName: string;
  shopId: number;
  existingPrice?: {
    price: string;
    is_discounted: boolean;
    price_before_discount?: string | null;
  } | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

const EnterPriceModal: React.FC<EnterPriceModalProps> = ({
  open,
  onClose,
  onSuccess,
  groceryId,
  groceryName,
  shopId,
  existingPrice,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Tab state - default to Manual tab
  const [tabValue, setTabValue] = useState(1);

  // Form state
  const [price, setPrice] = useState('');
  const [isDiscounted, setIsDiscounted] = useState(false);
  const [priceBeforeDiscount, setPriceBeforeDiscount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Scanner state
  const [scannerResetTrigger, setScannerResetTrigger] = useState(0);
  const [scannedPrice, setScannedPrice] = useState<string | null>(null);
  const [scannerActive, setScannerActive] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (open) {
      setPrice(existingPrice?.price || '');
      setIsDiscounted(existingPrice?.is_discounted || false);
      setPriceBeforeDiscount(existingPrice?.price_before_discount || '');
      setError(null);
      setScannedPrice(null);
      setScannerResetTrigger(0);
      // Default to manual tab
      setTabValue(1);
    }
  }, [open, existingPrice]);

  // When discount checkbox is checked, prefill original price from existing non-discounted price
  const handleDiscountChange = (checked: boolean) => {
    setIsDiscounted(checked);
    if (checked && !priceBeforeDiscount) {
      // Try to prefill from existing price data
      if (existingPrice) {
        if (!existingPrice.is_discounted) {
          // Existing price is not discounted - use it as the original price
          setPriceBeforeDiscount(existingPrice.price);
        } else if (existingPrice.price_before_discount) {
          // Existing price is discounted - use its original price
          setPriceBeforeDiscount(existingPrice.price_before_discount);
        }
      }
    }
  };

  // Debounce the scanner activation
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (open && tabValue === 0) {
      // Delay activating scanner to let modal settle
      timeoutId = setTimeout(() => {
        setScannerActive(true);
      }, 200);
    } else {
      setScannerActive(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [open, tabValue]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
  };

  // Handle price detected from scanner
  const handlePriceDetected = (detectedPrice: string) => {
    setScannedPrice(detectedPrice);
    setPrice(detectedPrice);
    // Auto-switch to manual tab to review/confirm
    setTabValue(1);
  };

  // Reset scanner to scan again
  const handleScanAgain = () => {
    setScannedPrice(null);
    setScannerResetTrigger((prev) => prev + 1);
    setTabValue(0);
  };

  const validate = (): boolean => {
    if (!price || isNaN(parseFloat(price)) || parseFloat(price) < 0) {
      setError('Please enter a valid price.');
      return false;
    }
    // Original price is optional, but if provided must be valid
    if (isDiscounted && priceBeforeDiscount && isNaN(parseFloat(priceBeforeDiscount))) {
      setError('Please enter a valid original price before discount.');
      return false;
    }
    // Original price must be higher than sale price
    if (isDiscounted && priceBeforeDiscount && parseFloat(priceBeforeDiscount) <= parseFloat(price)) {
      setError('Original price must be higher than the sale price.');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setError(null);

    const payload = {
      shop: shopId,
      grocery: groceryId,
      price: parseFloat(price).toFixed(2),
      is_discounted: isDiscounted,
      price_before_discount: isDiscounted && priceBeforeDiscount ? parseFloat(priceBeforeDiscount).toFixed(2) : null,
    };

    try {
      const savedPrice = await apiFetch(`${apiUrl}/api/prices/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      onSuccess(savedPrice);
      onClose();
    } catch (err: any) {
      setError(err.message || 'An error occurred while saving the price.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        {existingPrice ? 'Update Price' : 'Enter Price'}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {groceryName}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          aria-label="price entry method"
        >
          <Tab
            icon={<CameraAltIcon />}
            iconPosition="start"
            label={isMobile ? 'Scan' : 'Scan Price'}
            sx={{ minHeight: 48 }}
          />
          <Tab
            icon={<EditIcon />}
            iconPosition="start"
            label="Manual"
            sx={{ minHeight: 48 }}
          />
        </Tabs>
      </Box>

      {/* Scan Tab */}
      <TabPanel value={tabValue} index={0}>
        <PriceScanner
          onPriceDetected={handlePriceDetected}
          onError={(err) => setError(err)}
          active={scannerActive}
          resetTrigger={scannerResetTrigger}
        />

        {scannedPrice && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Price ${scannedPrice} detected! Review and save in the Manual tab.
          </Alert>
        )}
      </TabPanel>

      {/* Manual Tab */}
      <TabPanel value={tabValue} index={1}>
        {scannedPrice && (
          <Alert severity="info" sx={{ mb: 2 }} action={
            <Button color="inherit" size="small" onClick={handleScanAgain}>
              Scan Again
            </Button>
          }>
            Scanned price: ${scannedPrice}
          </Alert>
        )}

        <TextField
          label="Price"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          fullWidth
          required
          inputProps={{ min: 0, step: '0.01' }}
          sx={{ mb: 2 }}
          autoFocus={tabValue === 1 && !scannedPrice}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={isDiscounted}
              onChange={(e) => handleDiscountChange(e.target.checked)}
            />
          }
          label="This is a discounted price"
        />

        {isDiscounted && (
          <TextField
            label="Original Price (before discount)"
            type="number"
            value={priceBeforeDiscount}
            onChange={(e) => setPriceBeforeDiscount(e.target.value)}
            fullWidth
            placeholder="Optional"
            inputProps={{ min: 0, step: '0.01' }}
            sx={{ mt: 1 }}
            helperText="Leave blank if original price is unknown"
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button variant="outlined" color="primary" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save Price'}
          </Button>
        </Box>
      </TabPanel>
    </ModalWrapper>
  );
};

export default EnterPriceModal;
